import type { UnitType } from '@racemap/utilities/types/units';
import type {
  CircleLayerSpecification,
  ExpressionSpecification,
  SymbolLayerSpecification,
} from 'mapbox-gl';
import { type FC, useId, useMemo } from 'react';
import { Layer, Source } from 'react-map-gl/mapbox';
import {
  type IndexedLine,
  buildDistanceMarkerFeatures,
  visibleZoomBands,
} from '../helpers/distanceMarkers';

/** Every layer one instance adds starts with this, so callers can reorder them as a group. */
export const distanceMarkerLayerPrefix = (id: string): string => `layer-distance-marker-${id}-`;

export interface Props {
  line: IndexedLine;
  unitType: UnitType;
  /** Uniform index spacing in meters. Skips all distance measuring when known. */
  metersPerIndex?: number;
  /** Border of the bubble, to tie it to the track it measures. */
  strokeColor?: string;
  largeMode?: boolean;
  /** Hidden the mapbox way, not by unmounting: re-adding the layers re-uploads the GeoJSON. */
  visible?: boolean;
  id?: string;
  beforeId?: string;
}

export const DistanceMarkerLayer: FC<Props> = ({
  line,
  unitType,
  metersPerIndex,
  strokeColor = '#000000',
  largeMode = false,
  visible = true,
  id: settedId,
  beforeId,
}) => {
  const genId = useId();
  const id = settedId || genId;
  const featureCollection = useMemo(
    () => buildDistanceMarkerFeatures(line, unitType, metersPerIndex),
    [line, unitType, metersPerIndex],
  );
  const { circlePaint, textLayout } = useMemo(
    () => getMarkerStyle(largeMode, strokeColor),
    [largeMode, strokeColor],
  );
  const visibility = visible ? 'visible' : 'none';

  const bands = visibleZoomBands(featureCollection.features.length);
  const layerPrefix = distanceMarkerLayerPrefix(id);

  return (
    <Source
      type="geojson"
      data={featureCollection}
      id={`distance-markers-${id}`}
      key={`distance-markers-${id}`}
    >
      {/* A flat array, not fragments: <Source> injects its id into direct children only. */}
      {bands.flatMap((band) => [
        <Layer
          id={`${layerPrefix}circle-${band.stride}`}
          key={`${layerPrefix}circle-${band.stride}`}
          type="circle"
          filter={['==', ['%', ['get', 'distance'], band.stride], 0]}
          layout={{ visibility }}
          paint={circlePaint}
          minzoom={band.minzoom}
          maxzoom={band.maxzoom}
          beforeId={beforeId}
        />,
        <Layer
          id={`${layerPrefix}text-${band.stride}`}
          key={`${layerPrefix}text-${band.stride}`}
          type="symbol"
          filter={['==', ['%', ['get', 'distance'], band.stride], 0]}
          layout={{ ...textLayout, visibility }}
          paint={TEXT_PAINT}
          minzoom={band.minzoom}
          maxzoom={band.maxzoom}
          beforeId={beforeId}
        />,
      ])}
    </Source>
  );
};

/** Sizes the bubbles by zoom. Below and above the outer stops the size holds. */
const zoomRamp = (
  stops: Array<[zoom: number, size: number]>,
  scale: number,
): ExpressionSpecification =>
  [
    'interpolate',
    ['linear'],
    ['zoom'],
    ...stops.flatMap(([zoom, size]) => [zoom, size * scale]),
  ] as ExpressionSpecification;

/** Small enough at overview zooms to leave the route visible, readable once zoomed in. */
const CIRCLE_RADIUS_STOPS: Array<[number, number]> = [
  [10, 7],
  [13, 9],
  [17, 16],
];
const TEXT_SIZE_STOPS: Array<[number, number]> = [
  [10, 7],
  [13, 9],
  [17, 14],
];

function getMarkerStyle(
  largeMode: boolean,
  strokeColor: string,
): {
  circlePaint: CircleLayerSpecification['paint'];
  textLayout: SymbolLayerSpecification['layout'];
} {
  const scale = largeMode ? 1.5 : 1;

  return {
    circlePaint: {
      'circle-color': '#ffffff',
      'circle-radius': zoomRamp(CIRCLE_RADIUS_STOPS, scale),
      'circle-stroke-color': strokeColor,
      'circle-stroke-width': 2,
    },
    textLayout: {
      'symbol-placement': 'point',
      'text-field': '{distance}',
      'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Regular'],
      'text-anchor': 'center',
      'text-justify': 'center',
      'text-size': zoomRamp(TEXT_SIZE_STOPS, scale),
      'text-allow-overlap': true,
      'text-ignore-placement': true,
    },
  };
}

const TEXT_PAINT: SymbolLayerSpecification['paint'] = {
  'text-color': '#000000',
  'text-halo-color': '#ffffff',
  'text-halo-width': 1,
};
